import { Box, Tab, Tabs } from '@mui/material';
import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useParams } from 'react-router-dom';

import { TCompanyConfigs } from '../../../../shared/services/companyConfig/companyConfig.service.ts';
import { PrintFormsTabs } from '../states/tabState.model';
import { transitionService } from '../../../../shared/components/loading/service/transition.service.ts';
import { printFormsTabLoadingService } from '../printFormsTabLoading.service.ts';
import { navigateService } from '../../../../shared/services/navigate/navigate.service.ts';
import { TabContentContainer } from '../../../../shared/components/tabs/tabsContainer.component.tsx';
import { ContentLoading } from '../../../../shared/components/loading/loading.component.tsx';
import { TabPanel } from '../../../../shared/components/tabs';
import { GeneralTab } from './tabs/general/general.tab.tsx';
import { ReportsConfigurationTab } from './tabs/reportsConfiguration/reportsConfiguration.tab.tsx';
import { PrintersAssignment } from './tabs/printersAssignment/printersAssignment.tab.tsx';
import { companyConfigService } from '../../../../shared/services/companyConfig/companyConfig.service.ts';
import { EmailTab } from './tabs/email/email.tab.tsx';

const PrintFormsDetails: FC = () => {
  const { tab } = useParams() as { tab: PrintFormsTabs };
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const initTabData = useLoaderData() as TCompanyConfigs;
  const isPrintingAvailable = companyConfigService.getCachedConfigs()?.isPrintingAvailable;

  useEffect(() => {
    const unsubLoading = printFormsTabLoadingService.sub.loading().subscribe(({ isLoading }) => {
      setLoading(isLoading);
    });
    return () => {
      unsubLoading.unsubscribe();
    };
  }, []);

  const handleActiveTab: THandleActiveTab = (_, tab) => {
    transitionService.pub.off();
    printFormsTabLoadingService.pub.loading(true);
    navigateService.pub.navigateTo(`/settings/print-forms/${tab}`);
  };

  return (
    <>
      <Tabs
        scrollButtons={false}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant='scrollable'
        value={tab}
        onChange={handleActiveTab}
      >
        <Tab label={t('settings.general_tab')} value={PrintFormsTabs.general} />
        <Tab
          label={t('settings.reportConfigurationTab')}
          value={PrintFormsTabs.reportConfiguration}
        />
        {isPrintingAvailable && (
          <Tab
            label={t('settings.printers_assignment')}
            value={PrintFormsTabs.printersAssignment}
          />
        )}
        <Tab label={t('common.email')} value={PrintFormsTabs.email} />
      </Tabs>
      <TabContentContainer
        sx={{
          ...(tab === PrintFormsTabs.reportConfiguration && { overflowX: 'hidden' }),
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <ContentLoading {...{ loading }}>
            <TabPanel value={PrintFormsTabs.general} activeValue={tab}>
              <GeneralTab params={initTabData} />
            </TabPanel>
            <TabPanel value={PrintFormsTabs.reportConfiguration} activeValue={tab}>
              <ReportsConfigurationTab />
            </TabPanel>
            {isPrintingAvailable && (
              <TabPanel value={PrintFormsTabs.printersAssignment} activeValue={tab}>
                <PrintersAssignment />
              </TabPanel>
            )}
            <TabPanel value={PrintFormsTabs.email} activeValue={tab}>
              <EmailTab />
            </TabPanel>
          </ContentLoading>
        </Box>
      </TabContentContainer>
    </>
  );
};

export default PrintFormsDetails;

type THandleActiveTab = (evt: SyntheticEvent<Element, Event>, tab: string) => void;
